import { CalculatorFormValues } from 'common/types';
import * as validators from 'validators';
import { FC, useEffect, useState } from 'react';
import { Field } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components/macro';
import gsap from 'gsap';
import { containsOnlyNumbersChars } from 'utils';
import { WizardPageProps } from '../../WizardForm/WizardForm';
import { FieldError, Input, StepDescription, StepTitle } from '@net0-calc/common/components/ui';
import { DEVICE, TYPOGRAPHY } from '@net0-calc/common/assets/styles';

const fieldName = 'peopleCount';
const optionValues = [1, 2, 3, 4];

export const TravelersPage: FC<WizardPageProps<CalculatorFormValues>> = ({ values }) => {
  const { t } = useTranslation();
  const currentValue = values?.peopleCount;
  const [customInputLocal, setCustomInputLocal] = useState('');

  const isTileSelected = typeof currentValue === 'number' && optionValues.includes(currentValue);
  useEffect(() => {
    if (isTileSelected) setCustomInputLocal('');
  }, [isTileSelected]);

  const customDisplayValue =
    customInputLocal !== ''
      ? customInputLocal
      : typeof currentValue === 'number' && !optionValues.includes(currentValue)
      ? String(currentValue)
      : '';

  useEffect(() => {
    if (typeof currentValue === 'number' && optionValues.includes(currentValue)) {
      gsap.fromTo(
        `#travelers-tile-anim-${currentValue}`,
        { transform: 'translate(-10%, -25%)', scale: 1 },
        { transform: 'translate(-100%, -50%)', scale: 0, duration: 0.4, ease: 'power2.out' },
      );
    }
  }, [currentValue]);

  return (
    <div>
      <Root>
        <StepTitle>{t('personal.pages.travelers.title')}</StepTitle>
        <StepDescription dangerouslySetInnerHTML={{ __html: t('personal.pages.travelers.subTitle') }} />
        <TilesRow>
          {optionValues.map(optionValue => (
            <Field key={optionValue} name={fieldName} type="radio" value={optionValue} validate={validators.isRequired}>
              {({ input }) => (
                <NumberTileLabel tabIndex={0} $isChecked={input.checked} htmlFor={`people-count-tile-${optionValue}`}>
                  <TileAnimationElement id={`travelers-tile-anim-${optionValue}`} />
                  <TileContent>
                    <TileInput
                      tabIndex={-1}
                      id={`people-count-tile-${optionValue}`}
                      type="radio"
                      {...input}
                      onChange={e => {
                        const value = e.currentTarget.value;
                        input.onChange(Number(value));
                      }}
                    />
                    <NumberTileNumber $isChecked={input.checked}>{optionValue}</NumberTileNumber>
                  </TileContent>
                </NumberTileLabel>
              )}
            </Field>
          ))}
        </TilesRow>
        <FieldError name="peopleCount" />
        <CustomDivider>{t('personal.pages.travelers.form.field.peopleCount.customLabel')}</CustomDivider>
        <Field name={fieldName} validate={validators.isRequired}>
          {field => {
            const input = {
              ...field.input,
              value: customDisplayValue,
              onChange: (event: React.ChangeEvent<HTMLInputElement>) => {
                const targetValue = event.currentTarget.value;
                if (targetValue.length === 0) {
                  setCustomInputLocal('');
                  field.input.onChange(undefined);
                  return;
                }
                if (containsOnlyNumbersChars(targetValue)) {
                  setCustomInputLocal(targetValue);
                  const num = Number(targetValue);
                  field.input.onChange(num > 0 ? num : undefined);
                }
              },
            };
            return (
              <StyledInput
                type="text"
                label={t('personal.pages.travelers.form.field.peopleCount.label')}
                {...field}
                input={input}
              />
            );
          }}
        </Field>
      </Root>
    </div>
  );
};

const Root = styled.div`
  width: 100%;
  margin-bottom: 70px;
`;

const TilesRow = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 24px;

  @media ${DEVICE.laptop} {
    gap: 20px;
    margin-bottom: 28px;
  }
`;

const NumberTileLabel = styled.label<{ $isChecked?: boolean }>`
  position: relative;
  z-index: 0;
  cursor: pointer;
  border-radius: 12px;
  border: 1px solid ${({ $isChecked }) => ($isChecked ? 'var(--color2)' : 'var(--color10)')};
  background-color: ${({ $isChecked }) => ($isChecked ? 'var(--color2)' : 'var(--color5)')};
  overflow: hidden;
  transition: border-color 0.25s ease-out, background-color 0.25s ease-out, box-shadow 0.25s ease-out;
  width: 80px;
  height: 80px;
  flex-shrink: 0;

  &:hover {
    border-color: ${({ $isChecked }) => ($isChecked ? 'var(--color2)' : 'var(--color9)')};
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  }

  @media ${DEVICE.laptop} {
    width: 100px;
    height: 100px;
  }
`;

const TileAnimationElement = styled.div`
  position: absolute;
  transform: translate(-25%, -25%);
  width: 500px;
  height: 500px;
  background-color: var(--color5);
  border-radius: 50%;
  pointer-events: none;
`;

const TileContent = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
`;

const TileInput = styled.input`
  position: absolute !important;
  margin: 0 !important;
  padding: 0 !important;
  width: 1px !important;
  height: 1px !important;
  white-space: nowrap !important;
  clip-path: inset(100%) !important;
  clip: rect(0 0 0 0) !important;
  overflow: hidden !important;
  outline: none;
`;

const NumberTileNumber = styled.span<{ $isChecked?: boolean }>`
  ${TYPOGRAPHY.titleSemibold28}
  color: ${({ $isChecked }) => ($isChecked ? 'var(--color5)' : 'var(--color6)')};
  transition: color 0.25s ease-out;
`;

const CustomDivider = styled.div`
  ${TYPOGRAPHY.bodyMedium16}
  color: var(--color6);
  margin-bottom: 12px;
`;

const StyledInput = styled(Input)`
  width: 260px;
  white-space: nowrap;
  margin: 0;
`;
